import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../api';
import Plotly from 'react-plotly.js';
import AdvancedDataTable from './ui/AdvancedDataTable';
import BuyIntelligenceManager from './BuyIntelligenceManager';
import UploadToDatabase from './UploadToDatabase';
import { FinancialDashboard } from './FinancialAnalysis';

const Dashboard = () => {
  const [dataSource, setDataSource] = useState('database');
  const [marketplace, setMarketplace] = useState('amazon');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('proveedor');
  const [providerAnalysis, setProviderAnalysis] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState('Todos los proveedores');
  const [notification, setNotification] = useState(null);
  const [viewOption, setViewOption] = useState('resumen'); // Nueva opción de visualización
  const [pivotTableData, setPivotTableData] = useState(null);
  const [temporalData, setTemporalData] = useState(null);
  const [skusData, setSkusData] = useState(null);
  const [showFullPivotTable, setShowFullPivotTable] = useState(false);
  const [pivotTableType, setPivotTableType] = useState('simplified'); // 'simplified' or 'complete'
  const [maxRowsToShow, setMaxRowsToShow] = useState(5000); // Límite configurable
  const [showAllRows, setShowAllRows] = useState(false); // Flag para mostrar todos

  const marketplaces = [
    { id: 'amazon', name: 'Amazon' },
    { id: 'mercadolibre', name: 'MercadoLibre' },
    { id: 'liverpool', name: 'Liverpool' },
    { id: 'walmart', name: 'Walmart' },
    { id: 'claro', name: 'Claro' }
  ];

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setNotification(null);
    try {
      const response = await dashboardAPI.loadFromDatabase(marketplace);
      setData(response);
      
      // Mostrar notificación de conexión exitosa
      const dbName = response.metadata?.source || 'data_science';
      const tableName = 'newton_pivot_table';
      console.log('Mostrando notificación:', { dbName, tableName }); // Debug
      setNotification({
        type: 'success',
        message: `Conectado a ${dbName} en tabla ${tableName}`,
        timestamp: new Date().toLocaleTimeString()
      });
      
      // Auto-ocultar notificación después de 5 segundos
      setTimeout(() => setNotification(null), 5000);
      
      await loadProviderAnalysis('Todos los proveedores'); // Cargar análisis inicial
    } catch (err) {
      setError(err.message);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProviderAnalysis = async (provider) => {
    setLoading(true);
    try {
      const response = await dashboardAPI.providerAnalysis(marketplace, provider);
      setProviderAnalysis(response);
    } catch (err) {
      console.error('Error loading provider analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  // Nuevas funciones para cargar datos específicos por opción de visualización
  const loadPivotTableData = async (provider, forceRefresh = false) => {
    setLoading(true);
    try {
      // Usar el endpoint correcto de tabla pivot con cache busting si se fuerza refresh
      const response = await dashboardAPI.providerPivotTable(
        marketplace, 
        provider === 'Todos los proveedores' ? null : provider,
        forceRefresh
      );
      
      // La respuesta ya viene en el formato correcto del backend
      setPivotTableData(response);
    } catch (err) {
      console.error('Error loading pivot table data:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTemporalData = async (provider) => {
    setLoading(true);
    try {
      const response = await dashboardAPI.providerTemporalAnalysis(marketplace, provider);
      setTemporalData(response);
    } catch (err) {
      console.error('Error loading temporal analysis:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSkusData = async (provider) => {
    setLoading(true);
    try {
      const response = await dashboardAPI.providerSkusAnalysis(marketplace, provider);
      setSkusData(response);
    } catch (err) {
      console.error('Error loading SKUs analysis:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Función para formatear valores de la tabla pivot
  const formatPivotCellValue = (value, columnName) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    // Formatear valores numéricos
    if (typeof value === 'number') {
      // Para columnas de años históricos y predicción
      if (columnName.startsWith('u_') || columnName === 'pred') {
        try { return Number.isFinite(value) ? value.toFixed(0) : String(value); } catch { return String(value); }
      }
      // Para columnas de stock y ventas
      if (['stock_actual', 'stock_transito', 'stock_final', 'venta_ultimos_30', 'sugerencia_resurtido'].includes(columnName)) {
        try { return value.toLocaleString(); } catch { return String(value); }
      }
      // Para otros valores numéricos
      try { return Number.isFinite(value) ? value.toFixed(2) : String(value); } catch { return String(value); }
    }
    
    // Formatear valores de texto
    if (typeof value === 'string') {
      // Para valores "NO ENCONTRADO"
      if (value === 'NO ENCONTRADO' || value === 'NO NEWTON') {
        return <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>{value}</span>;
      }
      // Para valores booleanos
      if (value === 'true' || value === 'false') {
        return value === 'true' ? 'Sí' : 'No';
      }
      // Para valores largos, truncar
      if (value.length > 50) {
        return value.substring(0, 50) + '...';
      }
      return value;
    }
    
    return String(value);
  };



  useEffect(() => {
    if (data && selectedProvider) {
      // Resetear el modo "mostrar todos" al cambiar de proveedor o vista
      setShowAllRows(false);
      
      // Cargar datos según la opción de visualización actual
      switch (viewOption) {
        case 'resumen':
          loadProviderAnalysis(selectedProvider);
          break;
        case 'tabla-pivot':
          loadPivotTableData(selectedProvider, false);
          break;
        case 'analisis-temporal':
          loadTemporalData(selectedProvider);
          break;
        case 'skus-proveedor':
          loadSkusData(selectedProvider);
          break;
        default:
          loadProviderAnalysis(selectedProvider);
      }
    }
  }, [selectedProvider, viewOption]);

  // Estado para los datos de los histogramas
  const [histogramData, setHistogramData] = useState({
    publications: null,
    skus: null
  });

  // Actualizar datos de histogramas cuando se actualice el análisis del proveedor
  useEffect(() => {
    if (providerAnalysis && providerAnalysis.charts_data) {
      const newHistogramData = {};
      
      // Preparar datos del histograma de SKUs
      if (providerAnalysis.charts_data.histogram_skus) {
        newHistogramData.skus = providerAnalysis.charts_data.histogram_skus;
      }

      // Preparar datos del histograma de identificadores (publicaciones)
      if (providerAnalysis.charts_data.histogram_identifiers) {
        newHistogramData.publications = providerAnalysis.charts_data.histogram_identifiers;
      }

      setHistogramData(newHistogramData);
    }
  }, [providerAnalysis]);

  return (
    <div className="main-container" style={{ 
      padding: 'var(--spacing-xl)',
      backgroundColor: 'var(--color-light-gray)',
      minHeight: '100vh'
    }}>
      {/* Notificación de conexión */}
      {notification && (
        <div className={`notification ${notification.type === 'success' ? 'success' : 'error'}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>
              {notification.type === 'success' ? '✅' : '❌'}
            </span>
            <div>
              <div>{notification.message}</div>
              <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '2px' }}>
                {notification.timestamp}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="content-header" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <h1 className="title-primary" style={{ 
          fontSize: 'var(--font-size-4xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-black)',
          margin: 0
        }}>
          Dashboard General – Visualización y Análisis
        </h1>
      </div>
      
      <div className="card" style={{ 
        backgroundColor: 'var(--color-white)',
        padding: 'var(--spacing-xl)',
        borderRadius: 'var(--border-radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        border: '1px solid var(--color-light-gray)'
      }}>
        <h3 className="title-secondary" style={{ 
          fontSize: 'var(--font-size-xl)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-black)',
          marginBottom: 'var(--spacing-lg)'
        }}>
          Fuente de datos:
        </h3>
        <div className="radio-group">
          <label className="radio-item">
            <input
              type="radio"
              className="radio-input"
              value="session"
              checked={dataSource === 'session'}
              onChange={(e) => setDataSource(e.target.value)}
            />
            Datos de sesión
          </label>
          <label className="radio-item">
            <input
              type="radio"
              className="radio-input"
              value="database"
              checked={dataSource === 'database'}
              onChange={(e) => setDataSource(e.target.value)}
            />
            Datos de Base de Datos
          </label>
          <label className="radio-item">
            <input
              type="radio"
              className="radio-input"
              value="upload"
              checked={dataSource === 'upload'}
              onChange={(e) => setDataSource(e.target.value)}
            />
            Cargar a Base de Datos
          </label>
        </div>

        {dataSource === 'database' && (
          <div>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'end', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  color: 'var(--text-secondary)',
                  fontWeight: 'var(--font-weight-medium)'
                }}>
                  Marketplace:
                </label>
                <select 
                  value={marketplace} 
                  onChange={(e) => setMarketplace(e.target.value)}
                  className="input-field"
                  style={{ width: 'auto', minWidth: '150px' }}
                >
                  {marketplaces.map(mp => (
                    <option key={mp.id} value={mp.id}>{mp.name}</option>
                  ))}
                </select>
              </div>
              <button 
                className={`btn btn-primary ${loading ? 'disabled' : ''}`}
                onClick={loadData} 
                disabled={loading}
              >
                {loading ? 'Cargando...' : 'Cargar Datos'}
              </button>
            </div>
          </div>
        )}

        {dataSource === 'upload' && (
          <UploadToDatabase 
            marketplaces={marketplaces}
            onUploadSuccess={() => {
              // Cambiar automáticamente a vista de base de datos después de carga exitosa
              setDataSource('database');
              loadData();
            }}
          />
        )}

        {error && (
          <div className="message message-error">
            {error}
          </div>
        )}

        {data && (
          <div>
            <div className="radio-group" style={{ justifyContent: 'center', marginBottom: '32px' }}>
              <button
                className={`btn ${activeTab === 'proveedor' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setActiveTab('proveedor')}
              >
                Análisis por Proveedor
              </button>
              <button
                className={`btn ${activeTab === 'general' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setActiveTab('general')}
              >
                Estadísticas Generales
              </button>
              <button
                className={`btn ${activeTab === 'financiero' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setActiveTab('financiero')}
              >
                💰 Análisis Financiero
              </button>
            </div>

            {activeTab === 'proveedor' && (
              <div>
                <div className="form-group">
                  <label className="form-label">
                    Seleccionar Proveedor:
                  </label>
                  <select 
                    className="form-select"
                    value={selectedProvider} 
                    onChange={(e) => setSelectedProvider(e.target.value)}
                    style={{ width: 'auto', minWidth: '200px' }}
                  >
                    <option value="Todos los proveedores">Todos los proveedores</option>
                    {data.providers && data.providers.map(provider => (
                      <option key={provider} value={provider}>{provider}</option>
                    ))}
                  </select>
                </div>

                {/* Selector de Tipo de Visualización - Replica Streamlit */}
                <div className="form-group" style={{ marginBottom: 'var(--spacing-lg)' }}>
                  <label className="form-label" style={{ marginBottom: 'var(--spacing-md)' }}>
                    Tipo de visualización:
                  </label>
                  <div className="radio-group">
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="viewOption"
                        value="resumen"
                        checked={viewOption === 'resumen'}
                        onChange={(e) => setViewOption(e.target.value)}
                      />
                      Resumen general
                    </label>
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="viewOption"
                        value="tabla-pivot"
                        checked={viewOption === 'tabla-pivot'}
                        onChange={(e) => setViewOption(e.target.value)}
                      />
                      Tabla pivot proveedor
                    </label>
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="viewOption"
                        value="skus-proveedor"
                        checked={viewOption === 'skus-proveedor'}
                        onChange={(e) => setViewOption(e.target.value)}
                      />
                      SKUs por proveedor
                    </label>
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="viewOption"
                        value="analisis-temporal"
                        checked={viewOption === 'analisis-temporal'}
                        onChange={(e) => setViewOption(e.target.value)}
                      />
                      Análisis temporal
                    </label>
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="viewOption"
                        value="analisis-precios"
                        checked={viewOption === 'analisis-precios'}
                        onChange={(e) => setViewOption(e.target.value)}
                      />
                      Análisis de precios
                    </label>
                  </div>
                </div>

                {/* Resumen general - Opción por defecto */}
                {viewOption === 'resumen' && providerAnalysis && (
                  <div>
                    <div className="card">
                      <h3 className="title-secondary">Análisis del Proveedor: {selectedProvider}</h3>
                      <div className="metrics-grid">
                        <div className="metric-card">
                          <div className="metric-value">{providerAnalysis.general_stats?.skus_count || 0}</div>
                          <div className="metric-label">Total de SKUs</div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-value">{providerAnalysis.general_stats?.identifiers_count || 0}</div>
                          <div className="metric-label">Total de Identificadores</div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-value">{providerAnalysis.general_stats?.total_providers || 1}</div>
                          <div className="metric-label">Total de Proveedores</div>
                        </div>
                      </div>
                    </div>

                    {/* Histogramas */}
                    {histogramData.publications && histogramData.skus && (
                      <div className="card" style={{ marginTop: 'var(--spacing-lg)' }}>
                        <h3 className="title-secondary">Histogramas de Distribución</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)', marginTop: 'var(--spacing-md)' }}>
                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--color-primary-blue)' }}>
                              Densidad de Publicaciones por Proveedor
                            </h4>
                            <Plotly
                              data={histogramData.publications.data}
                              layout={histogramData.publications.layout}
                              style={{ width: '100%', height: '400px' }}
                            />
                          </div>
                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--color-primary-blue)' }}>
                              Densidad de SKUs por Proveedor
                            </h4>
                            <Plotly
                              data={histogramData.skus.data}
                              layout={histogramData.skus.layout}
                              style={{ width: '100%', height: '400px' }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tabla pivot proveedor */}
                {viewOption === 'tabla-pivot' && (
                  <div className="card">
                    <h3 className="title-secondary">Tabla Pivot - {selectedProvider}</h3>
                    {loading && <div className="loading-spinner">Cargando tabla pivot...</div>}
                    {error && <div className="message message-error">{error}</div>}
                    {pivotTableData && pivotTableData.success && (
                      <div>
                        <div className="description" style={{ marginBottom: 'var(--spacing-md)' }}>
                          <p><strong>Registros:</strong> {pivotTableData.total_records}</p>
                          <p><strong>Identificadores únicos:</strong> {pivotTableData.total_identifiers}</p>
                          <p><strong>Período histórico:</strong> {pivotTableData.temporality?.last_historical_period ? new Date(pivotTableData.temporality.last_historical_period).toLocaleDateString() : 'N/A'}</p>
                          <p><strong>Período predicción:</strong> {pivotTableData.temporality?.first_prediction_period ? new Date(pivotTableData.temporality.first_prediction_period).toLocaleDateString() : 'N/A'}</p>
                        </div>

                        {/* Mensajes de debug como Streamlit */}
                        {pivotTableData.debug_info && (
                          <div style={{ marginBottom: 'var(--spacing-md)' }}>
                            {/* Mensaje de temporalidad global Newton - EXACTO como Streamlit */}
                            {pivotTableData.debug_info.temporal_message && (
                              <div className="system-message system-message-temporal" style={{ 
                                backgroundColor: 'var(--color-accent-orange)',
                                color: 'var(--color-white)',
                                border: '1px solid var(--color-accent-orange)',
                                borderRadius: 'var(--border-radius-lg)',
                                padding: 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-sm)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-semibold)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-sm)',
                                boxShadow: 'var(--shadow-sm)'
                              }}>
                                <span style={{ fontSize: 'var(--font-size-lg)' }}>🌍</span>
                                <span>{pivotTableData.debug_info.temporal_message.replace('🌍 **', '').replace('**', '')}</span>
                              </div>
                            )}
                            
                            {/* Mensaje principal de estadísticas */}
                            <div className="system-message system-message-main" style={{ 
                              backgroundColor: 'var(--color-primary-blue)',
                              color: 'var(--color-white)',
                              border: '1px solid var(--color-primary-blue)',
                              borderRadius: 'var(--border-radius-lg)',
                              padding: 'var(--spacing-md)',
                              marginBottom: 'var(--spacing-sm)',
                              fontSize: 'var(--font-size-sm)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--spacing-sm)',
                              boxShadow: 'var(--shadow-sm)'
                            }}>
                              <span style={{ fontSize: 'var(--font-size-lg)' }}>📊</span>
                              <span><strong>{pivotTableData.debug_info.main_message}</strong></span>
                            </div>
                            
                            {/* Mensaje de inventory_id */}
                            <div className="system-message system-message-inventory" style={{ 
                              backgroundColor: 'var(--color-accent-green)',
                              color: 'var(--color-white)',
                              border: '1px solid var(--color-accent-green)',
                              borderRadius: 'var(--border-radius-lg)',
                              padding: 'var(--spacing-md)',
                              fontSize: 'var(--font-size-sm)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--spacing-sm)',
                              boxShadow: 'var(--shadow-sm)'
                            }}>
                              <span style={{ fontSize: 'var(--font-size-lg)' }}>📊</span>
                              <span>{pivotTableData.debug_info.inventory_message.replace('📊 **', '').replace('**', '')}</span>
                            </div>
                            
                            {/* Estadísticas detalladas */}
                            <div style={{ 
                              marginTop: 'var(--spacing-sm)',
                              padding: 'var(--spacing-md)',
                              backgroundColor: 'var(--color-white)',
                              borderRadius: 'var(--border-radius-lg)',
                              border: '1px solid var(--color-light-gray)',
                              boxShadow: 'var(--shadow-sm)'
                            }}>
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                                gap: 'var(--spacing-sm)',
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-dark-gray)'
                              }}>
                                <span><strong>Con Newton:</strong> {pivotTableData.debug_info.statistics?.con_newton}</span>
                                <span><strong>Sin Newton:</strong> {pivotTableData.debug_info.statistics?.sin_newton}</span>
                                <span><strong>Con Inventory ID:</strong> {pivotTableData.debug_info.statistics?.con_inventory_id}</span>
                                <span><strong>Sin Inventory ID:</strong> {pivotTableData.debug_info.statistics?.sin_inventory_id}</span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Controles de tabla */}
                        <div className="form-group" style={{ marginBottom: 'var(--spacing-md)' }}>
                          <div style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* Selector de tipo de tabla */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                              <label style={{ 
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-medium)',
                                color: 'var(--color-text)'
                              }}>
                                Tipo de tabla:
                              </label>
                              <select 
                                value={pivotTableType} 
                                onChange={(e) => setPivotTableType(e.target.value)}
                                style={{
                                  padding: 'var(--spacing-xs) var(--spacing-sm)',
                                  borderRadius: 'var(--border-radius-sm)',
                                  border: '1px solid var(--color-border)',
                                  backgroundColor: 'var(--color-background)',
                                  color: 'var(--color-text)',
                                  fontSize: 'var(--font-size-sm)',
                                  cursor: 'pointer',
                                  transition: 'border-color 0.2s ease'
                                }}
                              >
                                <option value="simplified">Tabla Simplificada</option>
                                <option value="complete">Tabla Completa</option>
                              </select>
                            </div>
                            
                            {/* Botón de descarga eliminado - ahora está integrado en la tabla */}
                          </div>
                        </div>
                        
                        {/* Tabla avanzada con AG Grid */}
                        {(() => {
                          const isSimplified = pivotTableType === 'simplified';
                          const currentData = isSimplified 
                            ? pivotTableData.pivot_data_simplified 
                            : pivotTableData.pivot_data_complete;
                          // Derivar columnas desde el backend si vienen; si no, infiéralas del primer registro
                          const columns = isSimplified
                            ? (pivotTableData.columns_simplified || (Array.isArray(currentData) && currentData[0] ? Object.keys(currentData[0]) : []))
                            : (pivotTableData.columns_complete || (Array.isArray(currentData) && currentData[0] ? Object.keys(currentData[0]) : []));

                          // Control inteligente de filas: limitar solo si no está en modo "mostrar todo"
                          const totalRows = Array.isArray(currentData) ? currentData.length : 0;
                          const shouldLimit = !showAllRows && totalRows > maxRowsToShow;
                          const displayData = shouldLimit 
                            ? currentData.slice(0, maxRowsToShow)
                            : currentData;
                          const hiddenRows = shouldLimit ? totalRows - maxRowsToShow : 0;
                          
                          return currentData && currentData.length > 0 ? (
                            <>
                              {/* Advertencia de datos limitados + botón para cargar más */}
                              {shouldLimit && (
                                <div style={{
                                  backgroundColor: 'var(--color-accent-yellow)',
                                  border: '1px solid var(--color-accent-orange)',
                                  borderRadius: 'var(--border-radius-lg)',
                                  padding: 'var(--spacing-md)',
                                  marginBottom: 'var(--spacing-md)',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  gap: 'var(--spacing-sm)'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                                    <span style={{ fontSize: 'var(--font-size-lg)' }}>⚠️</span>
                                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-dark-gray)' }}>
                                      <strong>Mostrando {maxRowsToShow.toLocaleString()} de {totalRows.toLocaleString()} registros</strong> 
                                      {' '}({hiddenRows.toLocaleString()} ocultos para optimizar rendimiento)
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                                    <button
                                      onClick={() => setShowAllRows(true)}
                                      style={{
                                        padding: 'var(--spacing-xs) var(--spacing-md)',
                                        backgroundColor: 'var(--color-primary-blue)',
                                        color: 'var(--color-white)',
                                        border: 'none',
                                        borderRadius: 'var(--border-radius-sm)',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 'var(--font-weight-semibold)',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--color-primary-dark-blue)'}
                                      onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--color-primary-blue)'}
                                    >
                                      🔓 Cargar TODOS los registros
                                    </button>
                                    <span style={{ 
                                      fontSize: 'var(--font-size-xs)', 
                                      color: 'var(--color-medium-gray)',
                                      alignSelf: 'center',
                                      fontStyle: 'italic'
                                    }}>
                                      o usa 📥 Descargar CSV
                                    </span>
                                  </div>
                                </div>
                              )}

                              {/* Mensaje de éxito cuando se muestran todos */}
                              {showAllRows && totalRows > maxRowsToShow && (
                                <div style={{
                                  backgroundColor: 'var(--color-accent-green)',
                                  border: '1px solid var(--color-accent-green)',
                                  color: 'var(--color-white)',
                                  borderRadius: 'var(--border-radius-lg)',
                                  padding: 'var(--spacing-md)',
                                  marginBottom: 'var(--spacing-md)',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  gap: 'var(--spacing-sm)'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                                    <span style={{ fontSize: 'var(--font-size-lg)' }}>✅</span>
                                    <span style={{ fontSize: 'var(--font-size-sm)' }}>
                                      <strong>Mostrando TODOS los {totalRows.toLocaleString()} registros</strong>
                                      {' '}(puede tardar unos segundos en cargar)
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => setShowAllRows(false)}
                                    style={{
                                      padding: 'var(--spacing-xs) var(--spacing-sm)',
                                      backgroundColor: 'rgba(255,255,255,0.2)',
                                      color: 'var(--color-white)',
                                      border: '1px solid var(--color-white)',
                                      borderRadius: 'var(--border-radius-sm)',
                                      fontSize: 'var(--font-size-xs)',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    🔒 Volver a limitar
                                  </button>
                                </div>
                              )}

                              <AdvancedDataTable
                                data={displayData}
                                loading={loading}
                                marketplace={marketplace}
                                pagination={true}
                                pageSize={100}
                                pageSizeOptions={[30, 100, 500]}
                                searchable={true}
                                exportable={true}
                                height="65vh"
                                columns={columns}
                                onRowClick={(rowData, rowIndex) => {
                                  console.log('Fila seleccionada:', rowData, rowIndex);
                                }}
                              />
                            </>
                          ) : (
                            <div className="message message-info">No hay datos disponibles para mostrar.</div>
                          );
                        })()}
                      </div>
                    )}
                    {pivotTableData && pivotTableData.error && (
                      <div className="message message-warning">{pivotTableData.error}</div>
                    )}
                    
                    {/* BuyIntelligenceManager - Solo para tabla simplificada */}
                    {pivotTableData && pivotTableData.success && pivotTableType === 'simplified' && (
                      <div style={{ marginTop: 'var(--spacing-lg)' }}>
                        <BuyIntelligenceManager
                          marketplace={marketplace}
                          proveedor={selectedProvider !== 'Todos los proveedores' ? selectedProvider : null}
                          tableData={pivotTableData.pivot_data_simplified || []}
                          onDataUpdated={() => {
                            // Recargar datos de la tabla cuando se actualiza buy intelligence CON FORCE REFRESH
                            loadPivotTableData(selectedProvider, true);
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Análisis temporal */}
                {viewOption === 'analisis-temporal' && (
                  <div className="card">
                    <h3 className="title-secondary">Análisis Temporal - {selectedProvider}</h3>
                    {loading && <div className="loading-spinner">Cargando análisis temporal...</div>}
                    {error && <div className="message message-error">{error}</div>}
                    {temporalData && temporalData.success && (
                      <div>
                        <div className="description" style={{ marginBottom: 'var(--spacing-md)' }}>
                          <p><strong>Meses totales:</strong> {temporalData.summary?.total_months}</p>
                          <p><strong>Meses históricos:</strong> {temporalData.summary?.historical_months}</p>
                          <p><strong>Meses predicción:</strong> {temporalData.summary?.prediction_months}</p>
                          <p><strong>Años disponibles:</strong> {temporalData.summary?.available_years?.join(', ')}</p>
                        </div>
                        
                        {temporalData.charts && temporalData.charts.monthly_evolution && (
                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--color-primary-blue)' }}>
                              Evolución Temporal Mensual
                            </h4>
                            <Plotly
                              data={temporalData.charts.monthly_evolution.data}
                              layout={temporalData.charts.monthly_evolution.layout}
                              style={{ width: '100%', height: '500px' }}
                            />
                          </div>
                        )}
                        
                        {temporalData.temporal_data && temporalData.temporal_data.length > 0 && (
                          <div style={{ marginTop: 'var(--spacing-lg)' }}>
                            <h4 style={{ marginBottom: 'var(--spacing-sm)' }}>Datos Mensuales Detallados</h4>
                            <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              <table className="data-table">
                                <thead>
                                  <tr>
                                    <th>Mes</th>
                                    <th>Tipo</th>
                                    <th>SKUs</th>
                                    <th>Datos Históricos</th>
                                    <th>Predicción</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {temporalData.temporal_data.map((monthData, index) => (
                                    <tr key={index}>
                                      <td>{monthData.month_name}</td>
                                      <td>{monthData.is_prediction ? 'Predicción' : 'Histórico'}</td>
                                      <td>{monthData.total_skus}</td>
                                      <td>
                                        {Object.entries(monthData.historical_data).map(([year, value]) => (
                                          <span key={year}>{year}: {value} </span>
                                        ))}
                                      </td>
                                      <td>{monthData.prediction_data || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {temporalData && temporalData.error && (
                      <div className="message message-warning">{temporalData.error}</div>
                    )}
                  </div>
                )}

                {/* SKUs por proveedor */}
                {viewOption === 'skus-proveedor' && (
                  <div className="card">
                    <h3 className="title-secondary">SKUs por Proveedor - {selectedProvider}</h3>
                    {loading && <div className="loading-spinner">Cargando análisis de SKUs...</div>}
                    {error && <div className="message message-error">{error}</div>}
                    {skusData && skusData.success && (
                      <div>
                        <div className="description" style={{ marginBottom: 'var(--spacing-md)' }}>
                          <p><strong>Total proveedores:</strong> {skusData.totals?.total_providers}</p>
                          <p><strong>SKUs únicos:</strong> {skusData.totals?.total_unique_skus}</p>
                          <p><strong>Identificadores:</strong> {skusData.totals?.total_identifiers}</p>
                          <p><strong>Total registros:</strong> {skusData.totals?.total_records}</p>
                        </div>
                        
                        {skusData.charts && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)', marginBottom: 'var(--spacing-lg)' }}>
                            <div>
                              <h4 style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--color-primary-blue)' }}>
                                SKUs Únicos por Proveedor
                              </h4>
                              <Plotly
                                data={skusData.charts.skus_by_provider.data}
                                layout={skusData.charts.skus_by_provider.layout}
                                style={{ width: '100%', height: '400px' }}
                              />
                            </div>
                            <div>
                              <h4 style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--color-primary-blue)' }}>
                                Identificadores Únicos por Proveedor
                              </h4>
                              <Plotly
                                data={skusData.charts.identifiers_by_provider.data}
                                layout={skusData.charts.identifiers_by_provider.layout}
                                style={{ width: '100%', height: '400px' }}
                              />
                            </div>
                          </div>
                        )}
                        
                        {skusData.summary && skusData.summary.length > 0 && (
                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-sm)' }}>Resumen por Proveedor</h4>
                            <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              <table className="data-table">
                                <thead>
                                  <tr>
                                    <th>Proveedor</th>
                                    <th>Total Registros</th>
                                    <th>SKUs Únicos</th>
                                    <th>Identificadores Únicos</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {skusData.summary.map((providerData, index) => (
                                    <tr key={index}>
                                      <td>{providerData.proveedor}</td>
                                      <td>{providerData.total_registros}</td>
                                      <td>{providerData.skus_unicos}</td>
                                      <td>{providerData.identificadores_unicos}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {skusData && skusData.error && (
                      <div className="message message-warning">{skusData.error}</div>
                    )}
                  </div>
                )}

                {/* Análisis de precios - Placeholder */}
                {viewOption === 'analisis-precios' && (
                  <div className="card">
                    <h3 className="title-secondary">Análisis de Precios - {selectedProvider}</h3>
                    <div className="message message-info">
                      Esta funcionalidad estará disponible próximamente. Permite analizar precios, costos, márgenes y rentabilidad por proveedor.
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'general' && (
              <div className="card">
                <h3 className="title-secondary">Estadísticas Generales</h3>
                <div className="description">
                  <p><strong>Total de Proveedores:</strong> {data.totalProviders}</p>
                  <p><strong>Total de SKUs:</strong> {data.totalSkus}</p>
                  <p><strong>Total de Publicaciones:</strong> {data.totalPublications}</p>
                  <p><strong>Fecha de Última Actualización:</strong> {data.lastUpdate}</p>
                </div>
              </div>
            )}

            {activeTab === 'financiero' && (
              <FinancialDashboard marketplace={marketplace} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;